import { Auth, Hub } from 'aws-amplify';
import { type FunctionComponent, type PropsWithChildren } from 'react';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import LoadingScreenOverlay from '../components/LoadingScreenOverlay';
import { wipeFlightSupplementalDataManager } from '../Datasource/FlightSupplementalData/FlightSupplementalDataManager';
import { wipeDbSyncer } from '../Datasource/LocalDB/DbSyncer';
import { getDb } from '../Datasource/LocalDB/LocalDB';
import {
    COGNITO_USER_SIGN_OUT,
    COGNITO_USER_STATE_PROCESSING,
    useCognitoAnyUserState,
} from '../reducers/cognitoUserReducer';
import { TLB_USER_SIGN_OUT, TLB_USER_STATE_PROCESSING, useAnyTlbUserState } from '../reducers/tlbUserReducer';
import { clearExternalOfpData } from '../screens/Import/Ofp/ExternalOfpTools';
import LoginScreen from '../screens/LoginScreen/LoginScreen';
import { isGenuineAuthInvalidationError } from '../utils/Authentication/isGenuineAuthInvalidationError';
import { reauthenticate } from '../utils/Authentication/Reauthenticate';
import { setPreferredEnvironment } from '../utils/BackendSwitcher';
import { clearImpersonationRecord, isImpersonating } from '../utils/Impersonation/impersonation';

const WithAuthenticatedUser: FunctionComponent<PropsWithChildren> = ({ children }) => {
    const cognitoUser = useCognitoAnyUserState();
    const tlbUser = useAnyTlbUserState();
    const dispatch = useDispatch();
    useEffect(() => {
        reauthenticate(dispatch);
        const onAuth = async (e: any) => {
            if (e.payload.event) {
                switch (e.payload.event) {
                    case 'signIn': {
                        reauthenticate(dispatch);

                        break;
                    }
                    case 'signOut': {
                        const wasImpersonating = isImpersonating();
                        clearImpersonationRecord();
                        clearExternalOfpData();
                        wipeDbSyncer();
                        wipeFlightSupplementalDataManager();
                        await getDb().wipe();
                        await getDb().errorLogs.clear();
                        dispatch({ type: TLB_USER_SIGN_OUT });
                        dispatch({ type: COGNITO_USER_SIGN_OUT });
                        setPreferredEnvironment(undefined);
                        // the impersonation record is held in memory; only a reload drops it (see impersonation.ts)
                        if (wasImpersonating) {
                            globalThis.location.reload();
                        }

                        break;
                    }
                    case 'tokenRefresh_failure': {
                        // Amplify fires this whenever a background token refresh fails, with the
                        // real underlying error attached - unlike Auth.currentAuthenticatedUser(),
                        // which collapses every failure (including a dropped connection) into the
                        // same generic "not authenticated" string. Only sign out here when the
                        // error actually says the refresh token is no longer valid; a transient
                        // network failure on weak signal must not sign the user out.
                        if (isGenuineAuthInvalidationError(e.payload.data)) {
                            Auth.signOut();
                        }

                        break;
                    }
                }
            }
        };
        Hub.listen('auth', onAuth);

        return () => {
            Hub.remove('auth', onAuth);
        };
    }, [dispatch]);

    if (cognitoUser === COGNITO_USER_STATE_PROCESSING || tlbUser === TLB_USER_STATE_PROCESSING) {
        return <LoadingScreenOverlay />;
    } else if (typeof cognitoUser === 'object' && cognitoUser.groups) {
        return <>{children}</>;
    }

    return <LoginScreen />;
};

export default WithAuthenticatedUser;
